import { useState, useEffect } from 'react';
import { Search, Filter, Trash2, Eye, Archive, Zap, AlertTriangle, RefreshCw } from 'lucide-react';
import { formatFileSize, formatRelativeTime, formatUploadStatus, getFileTypeIcon, getCompressionStatus, calculateSpaceSaved } from '../utils/formatters';
import { apiClient } from '../api/client';
import { filesApi } from '../api/upload';
import { useAuthStore } from '../stores/authStore';
import { useNotifications } from '../hooks/useNotifications';

export const FilesPage = () => {
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const filesPerPage = 10;

  const fetchFiles = async (page: number = 1, search: string = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: filesPerPage.toString(),
        sortBy: 'uploadedAt',
        sortOrder: 'desc',
      });

      const response = await apiClient.get(`/files?${params}`);
      if (response.data.success) {
        let fileData = response.data.data;
        
        // Apply client-side search filtering if needed
        if (search) {
          fileData = fileData.filter((file: any) =>
            file.originalName.toLowerCase().includes(search.toLowerCase()) ||
            file.uploadedBy.toLowerCase().includes(search.toLowerCase())
          );
        }

        setFiles(fileData);
        setTotalPages(response.data.meta?.totalPages || 1);
        setTotalFiles(response.data.meta?.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setFiles([]);
      setTotalPages(1);
      setTotalFiles(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles(currentPage, searchTerm);
  }, [currentPage]);

  useEffect(() => {
    // Reset to page 1 when search changes
    setCurrentPage(1);
    fetchFiles(1, searchTerm);
  }, [searchTerm]);

  const paginatedFiles = files;

  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const selectAllFiles = () => {
    if (selectedFiles.length === paginatedFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(paginatedFiles.map(file => file.id));
    }
  };

  const deleteFile = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }

    try {
      await filesApi.deleteFile(fileId);
      // Refresh the file list
      await fetchFiles(currentPage, searchTerm);
      // Clear selection if the deleted file was selected
      setSelectedFiles(prev => prev.filter(id => id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
      showError('Delete Failed', 'Failed to delete file. Please try again.');
    }
  };

  const deleteSelectedFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedFiles.length} file(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete files one by one
      for (const fileId of selectedFiles) {
        await filesApi.deleteFile(fileId);
      }
      // Refresh the file list
      await fetchFiles(currentPage, searchTerm);
      // Clear selection
      setSelectedFiles([]);
    } catch (error) {
      console.error('Failed to delete selected files:', error);
      showError('Bulk Delete Failed', 'Failed to delete some files. Please try again.');
    }
  };

  const syncAvailability = async () => {
    if (!user?.isAdmin) return;
    
    setSyncing(true);
    try {
      const response = await filesApi.syncAvailability();
      if (response.success) {
        showSuccess('Sync Complete', response.data.message || 'File availability sync completed successfully!');
        // Refresh the file list to show updated availability status
        await fetchFiles(currentPage, searchTerm);
      }
    } catch (error) {
      console.error('Failed to sync file availability:', error);
      showError('Sync Failed', 'Failed to sync file availability. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File History</h1>
          <p className="text-gray-600 mt-1">
            Browse and manage your uploaded files
          </p>
        </div>
        {user?.isAdmin && (
          <button
            onClick={syncAvailability}
            disabled={syncing}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Availability'}
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search files by name or uploader..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <button className="btn-secondary">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* File List */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Files ({totalFiles})
            </h2>
            {selectedFiles.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedFiles.length} selected
                </span>

                <button 
                  className="btn-danger text-sm"
                  onClick={deleteSelectedFiles}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedFiles.length === paginatedFiles.length && paginatedFiles.length > 0}
                    onChange={selectAllFiles}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compression
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Upload Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Loading files...
                  </td>
                </tr>
              ) : paginatedFiles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {searchTerm ? 'No files found matching your search.' : 'No files uploaded yet.'}
                  </td>
                </tr>
              ) : paginatedFiles.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => toggleFileSelection(file.id)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <span className="text-2xl">{getFileTypeIcon(file.mimeType, file.isCompressed)}</span>
                        {file.isCompressed && (
                          <Zap className="absolute -top-1 -right-1 w-3 h-3 text-brand-500" />
                        )}
                        {file.wasOriginallyZip && !file.isCompressed && (
                          <Archive className="absolute -top-1 -right-1 w-3 h-3 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                        <p className="text-xs text-gray-500">{file.mimeType}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <span className="font-mono">{formatFileSize(file.size)}</span>
                      {file.originalSize && file.originalSize !== file.size && (
                        <div className="text-xs text-gray-500">
                          from {formatFileSize(file.originalSize)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        file.isCompressed 
                          ? 'bg-brand-100 text-brand-800'
                          : file.wasOriginallyZip
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {file.isCompressed && <Zap className="w-3 h-3 mr-1" />}
                        {file.wasOriginallyZip && !file.isCompressed && <Archive className="w-3 h-3 mr-1" />}
                        {getCompressionStatus(file)}
                      </span>
                      {file.isCompressed && file.compressionRatio && file.compressionRatio > 1 && (
                        <div className="text-xs text-success-600 font-medium mt-1">
                          {calculateSpaceSaved(file.compressionRatio)} saved
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{file.uploadedBy}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatRelativeTime(file.uploadedAt)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge ${file.status.toLowerCase()}`}>
                      {formatUploadStatus(file.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {file.isAvailable === false && (
                        <span className="text-warning-600" title="File not available (may have been deleted externally)">
                          <AlertTriangle className="w-4 h-4" />
                        </span>
                      )}
                      <button 
                        className={file.isAvailable === false 
                          ? "text-gray-400 cursor-not-allowed" 
                          : "text-brand-600 hover:text-brand-700"
                        }
                        disabled={file.isAvailable === false}
                        title={file.isAvailable === false ? "File not available" : "View details"}
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {file.isAvailable !== false && (
                        <button 
                          className="text-error-600 hover:text-error-700"
                          onClick={() => deleteFile(file.id)}
                          title="Delete file"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * filesPerPage) + 1} to {Math.min(currentPage * filesPerPage, totalFiles)} of{' '}
                {totalFiles} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};